import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import type { PlaybackProvider, PlayerEvent } from '@mlm/shared';
import { EMPTY_SNAPSHOT, type PlaybackAdapter, type PlayerSnapshot } from './adapter';
import { createNativeAdapter } from './native-adapter';
import { createEmbedAdapter } from './embed-adapter';

type UsePlayerOptions = {
  kind: 'video' | 'iframe';
  provider: PlaybackProvider;
  src: string;
  /** Stable per lesson. The adapter is rebuilt when it changes, NOT when `src` does. */
  protocolId: string;
  onEvent?: (event: PlayerEvent, snapshot: PlayerSnapshot) => void;
};

type UsePlayerResult = {
  videoRef: RefObject<HTMLVideoElement>;
  iframeRef: RefObject<HTMLIFrameElement>;
  adapter: PlaybackAdapter | null;
  snapshot: PlayerSnapshot;
};

/**
 * Owns the adapter's lifetime and mirrors its snapshot into React state.
 *
 * Two deliberate choices: the adapter is keyed on the LESSON, not the `src`, so
 * a mid-lesson presigned-URL refresh swaps bytes without tearing down the
 * playback session; and `time` events are coalesced to one render per animation
 * frame, because a 4 Hz (embed) or 60 Hz (native) setState around a playing
 * video re-creates every prop-derived effect in the host.
 */
export function usePlayer({
  kind,
  provider,
  src,
  protocolId,
  onEvent,
}: UsePlayerOptions): UsePlayerResult {
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [adapter, setAdapter] = useState<PlaybackAdapter | null>(null);
  const [snapshot, setSnapshot] = useState<PlayerSnapshot>(EMPTY_SNAPSHOT);

  // Mirrored so the subscription never re-binds when the host re-renders.
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  // Read once at creation; a later refresh must not rebuild the adapter.
  const srcRef = useRef(src);
  srcRef.current = src;

  useLayoutEffect(() => {
    const instance =
      kind === 'video'
        ? videoRef.current
          ? createNativeAdapter(videoRef.current)
          : null
        : iframeRef.current
          ? createEmbedAdapter(iframeRef.current, { provider, src: srcRef.current, protocolId })
          : null;
    if (!instance) return;

    let frame: number | null = null;
    let dirty = false;
    const flush = () => {
      frame = null;
      dirty = false;
      setSnapshot({ ...instance.getSnapshot() });
    };
    const unsubscribe = instance.subscribe((event, snap) => {
      onEventRef.current?.(event, snap);
      // Coalesce the high-frequency ones; everything else lands immediately.
      if (event.type === 'time') {
        if (!dirty) {
          dirty = true;
          frame = requestAnimationFrame(flush);
        }
        return;
      }
      setSnapshot({ ...instance.getSnapshot() });
    });

    setAdapter(instance);
    setSnapshot({ ...instance.getSnapshot() });
    return () => {
      if (frame != null) cancelAnimationFrame(frame);
      unsubscribe();
      instance.destroy();
      setAdapter(null);
    };
  }, [kind, provider, protocolId]);

  // A cheap poll keeps the scrubber honest while the YouTube adapter is
  // extrapolating between deliveries (it patches the snapshot without emitting).
  useEffect(() => {
    if (!adapter || kind !== 'iframe') return;
    const timer = setInterval(() => setSnapshot({ ...adapter.getSnapshot() }), 250);
    return () => clearInterval(timer);
  }, [adapter, kind]);

  return { videoRef, iframeRef, adapter, snapshot };
}

/** Auto-hiding chrome + the transient in-frame notice, both timer-driven. */
export function useAutoHide(playing: boolean, idleMs = 2500) {
  const [visible, setVisible] = useState(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback(() => {
    setVisible(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setVisible(false), idleMs);
  }, [idleMs]);

  useEffect(() => {
    if (!playing) {
      if (timer.current) clearTimeout(timer.current);
      setVisible(true);
      return;
    }
    show();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [playing, show]);

  return { visible, show, toggle: () => (visible ? setVisible(false) : show()) };
}

/**
 * A message that fades on its own. The app-root `<Toaster/>` lives OUTSIDE the
 * player wrapper, so in fullscreen the browser renders none of it — anything
 * the learner must see while fullscreen has to be painted inside the frame.
 */
export function useFlash(ms = 2200) {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flash = useCallback(
    (next: string) => {
      setMessage(next);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setMessage(null), ms);
    },
    [ms],
  );
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return { message, flash };
}
